/**
 * Freezing a party onto a document.
 *
 * A snapshot is the party's data AS IT WAS when the document was issued, and a
 * legal invoice must not change when the master record does — renaming a customer
 * must not silently rewrite the name on an invoice already filed.
 *
 * It exists as a function because this project got it wrong once: the importer
 * built the snapshot from the BUSINESS rather than the PARTY, so every customer's
 * GSTIN and address was dropped from the frozen record and an out-of-state
 * customer was billed as intra-state. That is a compliance defect produced by
 * four fields assembled in the wrong order, which is exactly the kind of thing a
 * named, tested function prevents and a bare object literal does not.
 *
 * PURE, so it is testable without a database.
 */
import type { Party, PartySnapshot } from '../db/types';

/** Freeze a party's own data — never the business's. */
export function toPartySnapshot(party: Party): PartySnapshot {
  return {
    id: party.id,
    name: party.name,
    // A customer with no GSTIN is ordinary and must stay undefined, not become
    // an empty string: "" prints as a blank on the invoice and "" in the
    // snapshot compares unequal to undefined, so the two must not be confused.
    ...(party.gstin ? { gstin: party.gstin } : {}),
    stateCode: party.stateCode,
    stateName: party.stateName,
    addressLines: [...party.billingAddressLines],
    ...(party.phone ? { phone: party.phone } : {}),
    ...(party.email ? { email: party.email } : {}),
  };
}
